import { MessCode, TypeRp } from '../types/enums';
import { ApiResponse, KtToken } from '../types/api-response';
import { SUCCESS_CODES, ERROR_CODES, ERROR_MESSAGES } from '../constants/api';

// Helper function to check if API response is successful
export function isApiSuccess(response: ApiResponse<any>): boolean { 
  return SUCCESS_CODES.includes(response.messCode as any) || response.messCode === MessCode.IsOK;
}

// Helper function to check if API response has error
export function isApiError(response: ApiResponse<KtToken>): boolean {
  return ERROR_CODES.includes(response.messCode as any) || !isApiSuccess(response);
} 

// Helper function to get error message from MessCode
export function getErrorMessage(messCode: MessCode): string {
  switch (messCode) {
    case MessCode.Exception:
      return ERROR_MESSAGES.SERVER_ERROR;
    case MessCode.HashCode:
    case MessCode.HashCodeFalse:
      return ERROR_MESSAGES.HASH_CODE_ERROR;
    case MessCode.AccountLocked:
      return ERROR_MESSAGES.ACCOUNT_LOCKED;
    case MessCode.LoginDateTime:
    case MessCode.ApiDateOut:
    case MessCode.ApiDateFalse:
      return ERROR_MESSAGES.API_DATE_ERROR;
    case MessCode.RefreshTokenExpired:
    case MessCode.AuthenticateNotMatchRefresh:
      return ERROR_MESSAGES.TOKEN_EXPIRED;
    case MessCode.LicenseExpired:
      return ERROR_MESSAGES.TOKEN_EXPIRED;
    case MessCode.NotExist:
    case MessCode.NoDaTaFound:
      return ERROR_MESSAGES.NOT_FOUND;
    case MessCode.Duplicate:
      return ERROR_MESSAGES.DUPLICATE_ERROR;
    case MessCode.MissingInput:
    case MessCode.NULLInput:
    case MessCode.InvalidData:
      return ERROR_MESSAGES.VALIDATION_ERROR;
    case MessCode.Not200:
    case MessCode.LIMITINGRATE:
      return ERROR_MESSAGES.LIMITTINGRATE_ERROR;
    case MessCode.NotHost:
      return ERROR_MESSAGES.NETWORK_ERROR;
    default:
      return ERROR_MESSAGES.SERVER_ERROR;
  }
} 

// Helper function to create API date string (yyyyMMddHHmmss)
export function createApiDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  
  return `${year}${month}${day}${hours}${minutes}${seconds}`;
}

// Helper function to create hash code for API security
export async function createHashCode(
  apiDate: string,
  username: string,
  password: string,
  additionalData: string = '0988'
): Promise<string> {
  const hashString = `${apiDate}|${username}|${password}|${apiDate}|${additionalData}|${apiDate}`;
  
  try {
    // Check if crypto.subtle is available (requires secure context)
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      // Use Web Crypto API for SHA256
      const encoder = new TextEncoder();
      const data = encoder.encode(hashString);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      
      return hashHex;
    } 
    // Fallback to a simple hash function when crypto.subtle is not available
    console.warn('crypto.subtle not available, using fallback hash function');
    return simpleHash(hashString);
  } catch (error) {  
    console.error('Error creating hash code:', error);
    // Fallback to simple hash if crypto fails
    return simpleHash(hashString);
  }
}

// Simple hash function as fallback when crypto.subtle is not available
function simpleHash(str: string): string {
  // Non-cryptographic hash without bitwise operations to satisfy lint rules
  // Polynomial rolling hash with modulo to keep number in safe range
  const MOD = 4294967296; // 2^32
  const BASE = 31;
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    const char = str.charCodeAt(i);
    hash = (hash * BASE + char) % MOD; 
  }
  // Convert to hex string, pad to 8 chars minimum
  return Math.floor(hash).toString(16).padStart(8, '0');
}
  
// Helper function to check permission bit
export function hasPermission(funcsTagActive: string, bitPosition: number): boolean {
  if (!funcsTagActive || bitPosition < 0 || bitPosition >= funcsTagActive.length) {
    return false;
  }
  return funcsTagActive[bitPosition] === '1';
}

// Helper function to decode all permissions from funcsTagActive
export function decodePermissions(funcsTagActive: string): {
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canExport: boolean;
  canImport: boolean;
  canApprove: boolean;
  canReject: boolean;
} {
  return {
    canView: hasPermission(funcsTagActive, 0),
    canCreate: hasPermission(funcsTagActive, 1),
    canUpdate: hasPermission(funcsTagActive, 2),
    canDelete: hasPermission(funcsTagActive, 3),
    canExport: hasPermission(funcsTagActive, 4),
    canImport: hasPermission(funcsTagActive, 5),
    canApprove: hasPermission(funcsTagActive, 6),
    canReject: hasPermission(funcsTagActive, 7),
  };
}

// Helper function to format Vietnamese date
export function formatVietnameseDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch {
    return dateString;
  }
}

// Helper function to format Vietnamese datetime
export function formatVietnameseDateTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return dateString;
  }
}

// Helper function to validate API response structure
export function validateApiResponse<T>(response: any): response is ApiResponse<T> {
  return (
    typeof response === 'object' &&
    response !== null &&
    'messCode' in response &&
    'typeRp' in response &&
    'message' in response &&
    'dataPage' in response &&
    'items' in response &&
    'lstOptionModelType' in response &&
    'oneItem' in response
  );
}

// Helper function to create empty API response
export function createEmptyApiResponse<T>(): ApiResponse<T> {
  return {
    messCode: MessCode.Unknown,
    typeRp: TypeRp.Unknown,
    message: null,
    messageGoiY: null,
    dataPage: {
      tableName: null,
      rowCount: 0,
      rowCountNoWhere: 0,
      skip: 0,
      top: 0,
      orderby: null,
      status: 'Unknown' as any,
      searchby: null,
      datefm: null
    },
    items: null,
    lstOptionModelType: null,
    oneItem: null
  };
}

// Helper function to extract error details from API response
export function extractErrorDetails(response: ApiResponse<any>): {
  code: MessCode;
  message: string;
  suggestion?: string;
} {
  return {
    code: response.messCode,
    message: response.message || getErrorMessage(response.messCode),
    suggestion: response.messageGoiY || undefined
  };
}
